import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
  styled,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Chip,
  Divider,
  Badge,
} from '@mui/material';
import {
  Dashboard,
  AdminPanelSettings,
  Groups,
  Storage,
  AccountCircle,
  Assignment,
  Approval,
  Checklist as ChecklistIcon,
  Business as BusinessIcon,
  MailOutline as EmailIcon,
  AllInbox,
  CardMembership,
} from '@mui/icons-material';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { NAVIGATION_ITEMS, UserRole, APP_NAME, STORAGE_KEYS, API_CONFIG, SOCKET_KEYS, DISABLE_SUBSCRIPTION } from '../helpers/constants';
import { LanguageConstants } from '@/helpers/LanguageConstants';
import { alertsService, AlertCounts } from '../services/alertsService';
import socketService from '../services/socketService';
import PlanDetailsModal from './PlanDetailsModal';

const drawerWidth = 240;
const closedDrawerWidth = 60; // Width when collapsed

interface SidebarProps {
  open: boolean;
  handleDrawerClose: () => void;
  handleDrawerOpen: () => void;
}

// Styled Drawer header for the toggle button
const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
  justifyContent: 'flex-start',
}));

const Sidebar: React.FC<SidebarProps> = ({ open, handleDrawerClose, handleDrawerOpen }) => {
  const { isDarkMode } = useTheme();
  const { user, logout, markSessionExpired } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [planDetailsModalOpen, setPlanDetailsModalOpen] = useState<boolean>(false);

  // Alert/notification counts from API
  const [alertCounts, setAlertCounts] = useState<AlertCounts>({
    tasks: 0,
    approvals: 0,
  });

  // Fetch alert counts function
  const fetchAlertCounts = async () => {
    try {
      const counts = await alertsService.getAllAlertCounts();
      setAlertCounts(counts);
    } catch (error) {
      if (error instanceof Error && error.message.includes('401')) {
        markSessionExpired();
        return;
      }
      console.error('Failed to fetch alert counts:', error);
    }
  };

  // Fetch alert counts on component mount and set up polling
  useEffect(() => {
    // Only fetch if user is authenticated
    if (!user) {
      return;
    }

    // Initial fetch
    fetchAlertCounts();

    // Set up polling interval (refresh every 30 seconds)
    const POLLING_INTERVAL = 30000; // 30 seconds
    const intervalId = setInterval(() => {
      fetchAlertCounts();
    }, POLLING_INTERVAL);

    // Cleanup interval on component unmount
    return () => {
      clearInterval(intervalId);
    };
  }, [user]);

  // Socket connection for real-time updates
  useEffect(() => {
    if (!user) {
      return;
    }

    // Connect to socket server
    const socket = socketService.connect(API_CONFIG.SOCKET_URL);
    
    if (socket) {
      // Listen for shay_realtime events (common socket communication)
      const handleSocketEvent = async (eventData: any) => {
        try {
          // Handle task and approval events
          switch (eventData.type) {
            case SOCKET_KEYS.TASK_CREATED:
            case SOCKET_KEYS.TASK_UPDATED:
            case SOCKET_KEYS.TASK_DELETED:
            case SOCKET_KEYS.APPROVAL_CREATED:
            case SOCKET_KEYS.APPROVAL_UPDATED:
            case SOCKET_KEYS.APPROVAL_DELETED:
              // Refresh alert counts when task/approval changes occur
              console.log('Task/Approval event detected, refreshing counts...');
              await fetchAlertCounts();
              break;
            default:
              // Ignore other event types
              break;
          }
        } catch (error) {
          console.error('Error handling socket event:', error);
        }
      };

      socketService.on('shay_realtime', handleSocketEvent);

      // Cleanup on component unmount
      return () => {
        socketService.off('shay_realtime', handleSocketEvent);
      };
    }
  }, [user]);

  // Get user data from localStorage as fallback
  const getUserData = () => {
    try {
      const storedData = localStorage.getItem(STORAGE_KEYS.LOGGED_IN_USER_DATA);
      if (storedData) {
        const parsedData = JSON.parse(storedData);
        return {
          name: parsedData.name || user?.name || 'User',
          email: parsedData.email_id || user?.email || 'No email',
          role: parsedData.role || user?.role || 'user',
          companyName: parsedData.company_name || parsedData.companyName || ''
        };
      }
    } catch (error) {
      console.error('Error parsing stored user data:', error);
    }
    return {
      name: user?.name || 'User',
      email: user?.email || 'No email',
      role: user?.role || 'user',
      companyName: ''
    };
  };

  const userData = getUserData();

  // Get initials function - single letter only
  const getInitials = (fullName: string) => {
    return fullName.charAt(0).toUpperCase();
  };

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleClose();
    logout();
    navigate('/login');
  };

  // Get icon component by name
  const getIconComponent = (iconName: string) => {
    switch (iconName) {
      case 'Dashboard':
        return Dashboard;
      case 'AdminPanelSettings':
        return AdminPanelSettings;
      case 'Email':
        return EmailIcon;
      case 'AllInbox':
        return AllInbox;
      case 'Groups':
        return Groups;
      case 'Storage':
        return Storage;
      case 'Assignment':
        return ChecklistIcon;
      case 'Approval':
        return () => (
          <Box sx={{ 
            width: '24px', 
            height: '24px', 
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Box sx={{ 
              width: '16px', 
              height: '8px', 
              borderLeft: '4px solid currentColor',
              borderBottom: '4px solid currentColor',
              transform: 'rotate(-45deg) translateY(-0.5px)',
              borderRadius: '1px'
            }} />
          </Box>
        );
      case 'CardMembership':
        return CardMembership;
      default:
        return Dashboard;
    }
  };

  // Get alert count for a specific navigation item
  const getAlertCount = (itemPath: string): number | undefined => {
    switch (itemPath) {
      case '/tasks':
        return alertCounts.tasks;
      case '/approvals':
        return alertCounts.approvals;
      default:
        return undefined;
    }
  };

  // Filter navigation items based on user role and subscription feature flag (hide Current Plan when subscription disabled)
  const getAccessibleNavigationItems = () => {
    let items = Object.values(NAVIGATION_ITEMS);
    // If no user, show all navigation items for demo purposes
    if (user) {
      items = items.filter(item => item.roles.includes(user.role as UserRole));
    }
    // Hide Current Plan Details when subscription/payment is disabled
    if (DISABLE_SUBSCRIPTION) {
      items = items.filter(item => item.path !== '#plan-details');
    }
    return items;
  };

  const accessibleItems = getAccessibleNavigationItems();

  return (
         <Drawer
       variant="permanent"
       sx={{
         width: open ? drawerWidth : closedDrawerWidth,
         flexShrink: 0,
         whiteSpace: 'nowrap',
         boxSizing: 'border-box',
         zIndex: 1202,
         transition: (theme) =>
           theme.transitions.create('width', {
             easing: theme.transitions.easing.sharp,
             duration: open ? theme.transitions.duration.enteringScreen : theme.transitions.duration.leavingScreen,
           }),
         '& .MuiDrawer-paper': {
           width: open ? drawerWidth : closedDrawerWidth,
           overflowX: 'hidden',
           backgroundColor: isDarkMode ? 'background.paper' : 'primary.main',
           color: isDarkMode ? 'text.primary' : 'white',
           transition: (theme) =>
             theme.transitions.create('width', {
               easing: theme.transitions.easing.sharp,
               duration: open ? theme.transitions.duration.enteringScreen : theme.transitions.duration.leavingScreen,
             }),
           display: 'flex',
           flexDirection: 'column',
         },
       }}
     >
      <DrawerHeader sx={{ 
        backgroundColor: isDarkMode ? 'background.paper' : 'white',
        color: 'text.primary',
        justifyContent: 'flex-start',
        pl: open ? 1 : 1
      }}>
        {open ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
            <Box
              component="img"
              src={isDarkMode ? '/fulllogo-white.png' : '/fulllogo.png'}
              alt={APP_NAME || 'App'}
              sx={{
                height: 30,
                width: 'auto',
                maxWidth: '100%',
                objectFit: 'contain',
              }}
            />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
            <Box
              component="img"
              src={isDarkMode ? '/logo-white.png' : '/logo.png'}
              alt={APP_NAME || 'App'}
              sx={{
                height: 24,
                width: 'auto',
                maxWidth: '100%',
                objectFit: 'contain',
              }}
            />
          </Box>
        )}
      </DrawerHeader>
      
      {/* Navigation Items */}
      <List sx={{ flexGrow: 1 }}>
        {accessibleItems.length > 0 ? (
          accessibleItems.map((item) => {
            const IconComponent = getIconComponent(item.icon);
            const isActive = location.pathname === item.path;
            const alertCount = getAlertCount(item.path);
            const hasAlerts = alertCount !== undefined && alertCount > 0;
            const isModalAction = (item as any).action === 'modal';
            
            return (
              <ListItem key={item.path} disablePadding sx={{ display: 'block' }}>
                <ListItemButton
                  component={isModalAction ? 'div' : RouterLink}
                  to={isModalAction ? undefined : item.path}
                  onClick={isModalAction ? () => {
                    if (item.path === '#plan-details') {
                      setPlanDetailsModalOpen(true);
                    }
                  } : undefined}
                  selected={isActive && !isModalAction}
                  sx={{
                    minHeight: 48,
                    justifyContent: open ? 'initial' : 'center',
                    px: 2.5,
                    color: isDarkMode ? 'text.primary' : 'white',
                    '&:hover': {
                      backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(255, 255, 255, 0.08)',
                    },
                    '&.Mui-selected': {
                      backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.12)' : 'rgba(255, 255, 255, 0.12)',
                      color: isDarkMode ? 'text.primary' : 'white',
                      '&:hover': {
                        backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.16)' : 'rgba(255, 255, 255, 0.16)',
                      },
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: open ? 3 : 'auto',
                      justifyContent: 'center',
                      color: isDarkMode ? 'text.primary' : 'white',
                    }}
                  >
                    <Badge
                      badgeContent={!open ? alertCount : undefined}
                      color="error"
                      invisible={!hasAlerts}
                      sx={{
                        '& .MuiBadge-badge': {
                          fontSize: '0.65rem',
                          fontWeight: 700,
                          height: '18px',
                          minWidth: '18px',
                          padding: '0 4px',
                          border: `2px solid ${isDarkMode ? 'background.paper' : 'primary.main'}`,
                          boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                        },
                      }}
                    >
                      <IconComponent />
                    </Badge>
                  </ListItemIcon>
                  <ListItemText 
                    primary={item.label} 
                    sx={{ opacity: open ? 1 : 0 }}
                    primaryTypographyProps={{
                      sx: { display: 'flex', alignItems: 'center', gap: 1 }
                    }}
                  />
                  {/* Badge for expanded sidebar */}
                  {open && hasAlerts && (
                    <Chip
                      label={alertCount}
                      size="small"
                      sx={{
                        height: '20px',
                        minWidth: '20px',
                        fontSize: '0.7rem',
                        fontWeight: 700,
                        bgcolor: 'error.main',
                        color: 'white',
                        '& .MuiChip-label': {
                          px: 0.75,
                        },
                        ml: 'auto',
                        opacity: open ? 1 : 0,
                      }}
                    />
                  )}
                </ListItemButton>
              </ListItem>
            );
          })
        ) : (
          // Fallback: Show at least one navigation item if none are accessible
          <ListItem disablePadding sx={{ display: 'block' }}>
            <ListItemButton
              component={RouterLink}
              to="/supportSpace"
              sx={{
                minHeight: 48,
                justifyContent: open ? 'initial' : 'center',
                px: 2.5,
                color: isDarkMode ? 'text.primary' : 'white',
                '&:hover': {
                  backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(255, 255, 255, 0.08)',
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: open ? 3 : 'auto',
                  justifyContent: 'center',
                  color: isDarkMode ? 'text.primary' : 'white',
                }}
              >
                <AllInbox />
              </ListItemIcon>
              <ListItemText primary={LanguageConstants.topic_s} sx={{ opacity: open ? 1 : 0 }} />
            </ListItemButton>
          </ListItem>
        )}
      </List>

      {/* Profile Section - Fixed at Bottom */}
      <Box
        sx={{
          borderTop: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.12)' : 'rgba(255, 255, 255, 0.12)'}`,
          p: 1,
        }}
      >
        <ListItem disablePadding sx={{ display: 'block' }}>
          <ListItemButton
            onClick={handleMenu}
            sx={{
              minHeight: 48,
              justifyContent: open ? 'initial' : 'center',
              px: 2.5,
              color: isDarkMode ? 'text.primary' : 'white',
              '&:hover': {
                backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(255, 255, 255, 0.08)',
              },
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: open ? 3 : 'auto',
                justifyContent: 'center',
                color: isDarkMode ? 'text.primary' : 'white',
              }}
            >
              <Avatar 
                sx={{ 
                  width: 32, 
                  height: 32, 
                  bgcolor: isDarkMode ? 'primary.main' : 'white',
                  color: isDarkMode ? 'white' : 'primary.main',
                  fontSize: '0.9rem',
                  fontWeight: 600
                }}
              >
                {getInitials(userData.name)}
              </Avatar>
            </ListItemIcon>
            <ListItemText 
              primary={userData.name} 
              sx={{ 
                opacity: open ? 1 : 0,
                '& .MuiListItemText-primary': {
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }
              }} 
            />
          </ListItemButton>
        </ListItem>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          PaperProps={{
            sx: {
              minWidth: 320,
              mt: 1,
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
              borderRadius: 2,
            }
          }}
        >
          {/* Profile Card Header - Common App Layout */}
          <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
              {/* Profile Avatar */}
              <Avatar 
                sx={{ 
                  width: 40, 
                  height: 40, 
                  bgcolor: 'primary.main',
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  flexShrink: 0
                }}
              >
                {getInitials(userData.name)}
              </Avatar>
              
              {/* Profile Information */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {/* Full Name */}
                <Typography 
                  variant="subtitle2" 
                  sx={{ 
                    fontWeight: 600, 
                    color: 'text.primary',
                    mb: 0.5,
                    lineHeight: 1.2,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {userData.name}
                </Typography>
                
                {/* Email with Icon */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                  <EmailIcon sx={{ fontSize: 14, color: 'text.secondary', flexShrink: 0 }} />
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: 'text.secondary',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      fontSize: '0.75rem'
                    }}
                  >
                    {userData.email}
                  </Typography>
                </Box>
                
                {/* Company Name with Icon */}
                {userData.companyName && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                    <BusinessIcon sx={{ fontSize: 14, color: 'text.secondary', flexShrink: 0 }} />
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: 'text.secondary',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontSize: '0.75rem'
                      }}
                    >
                      {userData.companyName}
                    </Typography>
                  </Box>
                )}
                
                {/* Role Chip */}
                <Chip
                  label={userData.role === 'admin' ? 'Admin' : 'User'}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.7rem',
                    fontWeight: 500,
                    bgcolor: userData.role === 'admin' ? 'primary.50' : 'success.50',
                    color: userData.role === 'admin' ? 'primary.700' : 'success.700',
                    border: '1px solid',
                    borderColor: userData.role === 'admin' ? 'primary.200' : 'success.200',
                    '& .MuiChip-label': {
                      px: 1,
                    }
                  }}
                />
              </Box>
            </Box>
          </Box>
          
          <Divider />
          
          <MenuItem 
            onClick={() => { handleClose(); navigate('/profile'); }} 
            sx={{ 
              py: 0.75, 
              px: 2,
              fontSize: '0.875rem',
              '&:hover': {
                backgroundColor: 'action.hover'
              }
            }}
          >
            <AccountCircle sx={{ mr: 2, fontSize: 18, color: 'text.secondary' }} />
            My Profile
          </MenuItem>
          
          <Divider />
          
          <MenuItem 
            onClick={() => { handleClose(); navigate('/settings'); }} 
            sx={{ 
              py: 0.75, 
              px: 2,
              fontSize: '0.875rem',
              '&:hover': {
                backgroundColor: 'action.hover'
              }
            }}
          >
            <SettingsIcon sx={{ mr: 2, fontSize: 18, color: 'text.secondary' }} />
            Settings
          </MenuItem>
          
          <Divider />
          
          <MenuItem 
            onClick={handleLogout} 
            sx={{ 
              py: 1.25, 
              px: 2,
              color: 'error.main',
              fontSize: '0.875rem',
              '&:hover': {
                backgroundColor: 'error.50'
              }
            }}
          >
            <LogoutIcon sx={{ mr: 2, fontSize: 18 }} />
            Logout
          </MenuItem>
        </Menu>
      </Box>

      {/* Plan Details Modal */}
      <PlanDetailsModal
        open={planDetailsModalOpen}
        onClose={() => setPlanDetailsModalOpen(false)}
      />
    </Drawer>
  );
};

export default Sidebar; 